import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../lib/utils';
import { Analytics } from '@vercel/analytics/react';
import Preloader from './Preloader';
import { useTheme } from '../context/ThemeContext';
import PWAInstallPrompt from './PWAInstallPrompt';
import Header from './Header';
import Footer from './Footer';
import ScrollToTopButton from './ScrollToTopButton';

type LayoutProps = {
  children: React.ReactNode;
};

/**
 * Main layout component that wraps all pages.
 * Provides consistent header, footer, and theme context.
 * 
 * Refactored to use extracted Header and Footer components
 * for better maintainability and reduced complexity.
 */
export default function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Track scroll position for header styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Memoized toggle handler
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  // Memoized close handler
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <div className={cn('flex flex-col min-h-screen', isDark ? 'dark' : '')}>
      <Analytics />
      <Preloader />
      <PWAInstallPrompt />

      <Header
        isDark={isDark}
        scrolled={scrolled}
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobileMenu={toggleMobileMenu}
        onCloseMobileMenu={closeMobileMenu}
      />

      <main 
        className="flex-grow dark:bg-gray-900 dark:text-white"
        style={{ paddingTop: 64 }}
      >
        {children}
      </main>

      <Footer />
      <ScrollToTopButton />
    </div>
  );
}
